import { agentsNumbers, CallAgentsNumbersByState } from "../lib/agent-registry";

/**
 * Checks if an email is valid, not a test email, and not gibberish.
 * @param {string} email - The email to validate.
 * @returns {boolean} - True if the email passes all checks, false otherwise.
 */
export function isValidEmail(email) {
  if (typeof email !== "string") return false;

  // Trim spaces
  const trimmedEmail = email.trim().toLowerCase();

  // 1️⃣ Basic RFC 5322 compliant regex for most valid emails
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmedEmail)) return false;

  // 2️⃣ Reject obvious test/demo emails
  const testPatterns = [
    "test", "example", "demo", "fake", "sample", "dummy"
  ];
  if (testPatterns.some((pattern) => trimmedEmail.includes(pattern))) return false;

  // 3️⃣ Gibberish detection: local-part without vowels (a, e, i, o, u)
  //   const localPart = trimmedEmail.split("@")[0];
  //   if (!/[aeiou]/.test(localPart) && localPart.length > 5) return false;

  // 4️⃣ Optional: Reject known disposable email domains
  const disposableDomains = [
    "mailinator.com", "tempmail.com", "10minutemail.com", "guerrillamail.com"
  ];
  const domain = trimmedEmail.split("@")[1];
  if (disposableDomains.includes(domain)) return false;

  return true;
}

/**
 * Validates if a phone number is a valid US number.
 * Allows formats with +1, 1, or no country code.
 * @param {string} phoneNumber
 * @returns {boolean}
 */
export function isValidUSPhoneNumber(phoneNumber) {
  if (typeof phoneNumber !== "string") return false;

  // Remove spaces, hyphens, parentheses
  const cleaned = phoneNumber.replace(/[\s()-]/g, "");

  // US phone pattern: optional +1 or 1, followed by 10 digits
  const usPhoneRegex = /^(?:\+1|1)?\d{10}$/;

  return usPhoneRegex.test(cleaned);
}

/**
 * Returns true if the provided name looks like a real human name.
 * Rejects common fakes (test/demo), placeholders, numeric/symbol noise, and gibberish.
 * @param {string} raw
 */
export function isPlausibleHumanName(raw) {
  if (typeof raw !== "string") return false;

  // normalize
  const name = raw.trim().replace(/\s+/g, " ");
  if (name.length < 2 || name.length > 64) return false;

  // array sandbox identity
  if (name === 'ATKZH' || name === 'OVRVQCX' || name === 'ATKZH OVRVQCX') return true

  // must be letters (unicode), spaces, apostrophes, or hyphens
  // no digits, underscores, slashes, @, etc.
  // eslint-disable-next-line
  // if (/[0-9_@/\\|{}\[\]<>~`^+=*#:;.,!?]/.test(name)) return false;

  // disallow starting/ending with hyphen/apostrophe/space
  // eslint-disable-next-line
  // if (/^[\s'\-]|[\s'\-]$/.test(name)) return false;

  // blacklist (case-insensitive) – add your own terms as needed
  const blacklist = new Set([
    "test", "tester", "testing", "demo", "sample", "fake", "invalid",
    "asdf", "qwerty", "zxcv", "lorem", "ipsum", "placeholder",
    "null", "undefined", "n/a", "na", "none", "anonymous", "anon",
    "xxx", "xxxx", "john doe", "jane doe"
  ]);
  const lc = name.toLowerCase();
  if (blacklist.has(lc)) return false;

  // reject if all characters are the same (e.g., "aaaaaa", "------")
  // if (/^([^\s])\1{2,}$/.test(name.replace(/ /g, ""))) return false;

  // reject excessive repeated chars (e.g., "Aaaaron", "Miiiiike")
  // if (/(.)\1{3,}/i.test(name)) return false;

  // basic vowel presence check to catch gibberish (“brrtzzk”)
  // allow names down to 2 chars; apply vowel heuristic for length >= 5
  // const lettersOnly = name.replace(/[^A-Za-z\u00C0-\u017F']/g, ""); // include Latin accents
  // const vowels = (lettersOnly.match(/[aeiouy\u00C0-\u017F]/gi) || []).length;
  // if (lettersOnly.length >= 5 && vowels / lettersOnly.length < 0.2) return false;

  // allow hyphen/apostrophe only between letters (e.g., O'Neil, Jean-Luc)
  // if (/['-]{2,}/.test(name)) return false; // no multiple punctuation
  // if (/[^A-Za-z\u00C0-\u017F]['-]|['-][^A-Za-z\u00C0-\u017F]/.test(name)) return false;

  // must contain at least one letter
  // if (!/[A-Za-z\u00C0-\u017F]/.test(name)) return false;

  return true;
}


/**
 * Returns true provided url parameters.
 * if keys not provided, returns entire url parameters
 * @param {Array} keys - an array of parameter keys
 */
export function extractUTMParameters(keys = []) {
  const urlParams = new URLSearchParams(window.location.search)

  if (keys.length === 0) return "&" + (window.location.href).split('?')[1]

  let params = '&'

  for (const [k, v] of urlParams)
    if (keys.includes(k))
      params += `${k}=${v}&`

  return params.substring(0, params.length - 1) // remove trailing &
}

export function getParentUrlParams() {
  let parentUrl = window.parent.location.href
  const parentUrlParams = parentUrl.split('?')[1]
  if (parentUrlParams.length > 1)
    return "&" + parentUrlParams // &utm_medium=facebook
  return ''
}

export const BuyersTYUrl = new Map([
  ['National Debt Relief', 'https://www.finaldebtrelief.com/success-valid-ndr-'],
  ["Accredited Debt Relief", 'https://www.finaldebtrelief.com/success-valid-adr-'],
  ['First Choice Debt Relief', 'https://www.finaldebtrelief.com/success-valid-fcdr-'],
  ['Turbo Debt', 'https://www.finaldebtrelief.com/success-turbo-'],
  ['Cura Debt', 'https://www.finaldebtrelief.com/success-cura-'],
])

export function saveTrustedFormCert(trustedFormCert, contactId) {
  if (trustedFormCert) {
    let tcert = encodeURIComponent(trustedFormCert)
    const url = `/api/users/${contactId}/update/trusted_form_cert_url/${tcert}`
    console.log(url)
    fetch(url, { credentials: "include" })
      .then(res => res.json())
      .then(res => {
        console.log(res.success)
      }).catch(err => {
        console.log(err.message)
      })
  }
}

export function initializeLeadConnector(contactId, firstname, lastname, email, phone) {
  window.lc = window.lc || {}
  // window.lc.debug = true // remove in prod
  window.lc.identity = {
    distinctId: contactId,
    email: email,
    name: firstname + " " + lastname,
    phone: phone
  }
}

export async function getData(url) {
  const res = await fetch(url, {
    credentials: "include",
    method: "GET"
  })

  if (!res.ok)
    throw Error(res.statusText)

  const response = await res.json()

  if (response.success)
    return response.data

  console.log(response.error)

  throw Error(response.msg)
}

/**
 * Converts seconds to a friendly h/m/s string.
 * Examples:
 *  - 245  -> "4m : 5s"
 *  - 3661 -> "1h : 1m : 1s"
 *  - 18   -> "18s"
 */
export function formatDuration(secondsInput) {
  const total = Math.max(0, Math.floor(Number(secondsInput) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  const parts = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (!hours && !minutes) {
    // If both are zero, still show seconds; otherwise push seconds after existing parts
    parts.push(`${seconds}s`);
  } else {
    parts.push(`${seconds}s`);
  }

  return parts.join(' : ');
}

/**
 * Converts 24-hour time (HH:MM) to 12-hour (h:MMam/pm).
 * Examples:
 *  - "14:26" -> "2:26pm"
 *  - "00:05" -> "12:05am"
 *  - "12:00" -> "12:00pm"
 */
export function to12Hour(time24) {
  if (typeof time24 !== "string" || !/^\d{1,2}:\d{2}$/.test(time24.trim())) return time24;
  const [hStr, mStr] = time24.trim().split(":");
  let h = Number(hStr);
  const m = mStr.padStart(2, "0");
  if (Number.isNaN(h) || Number.isNaN(Number(mStr))) return time24;

  const suffix = h >= 12 ? "pm" : "am";
  h = h % 12;
  if (h === 0) h = 12;

  return `${h}:${m}${suffix}`;
}

/**
 * Returns the current hour (0-23) in Eastern Time (America/New_York), DST aware.
 */
export function getCurrentHourET() {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour12: false,
    hour: "2-digit",
  });
  const parts = formatter.formatToParts(new Date());
  const hourPart = parts.find((p) => p.type === "hour")?.value;
  const hour = hourPart ? parseInt(hourPart, 10) : NaN;
  return Number.isNaN(hour) ? null : hour;
}

export function formatUSPhoneNumber(input) {
  if (!input) return "";

  // remove everything except digits
  let digits = input.replace(/\D/g, "");

  // strip leading country code (1 or +1)
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.substring(1);
  }

  // must be exactly 10 digits now
  if (digits.length !== 10) return input; // fallback: return original

  const area = digits.substring(0, 3);
  const prefix = digits.substring(3, 6);
  const line = digits.substring(6);

  return `(${area}) ${prefix}-${line}`;
}

export async function tagContactSoftCredPull(contact_id, trial) {
  try {
    const res = await fetch(`/webhook/ghl/tag/soft_credit_pull?contact_id=${contact_id}&trial=${trial}`, {
      credentials: "include",
      method: "GET"
    })

    if (!res.ok)
      throw Error(res.statusText)

    const response = await res.json()

    if (response.success)
      return response.data

    console.log(response.error)

    throw Error(response.msg)
  } catch (error) {
    console.log(error)
  }
}

export async function triggerRetell1(contact_id) {
  try {
    const res = await fetch(`/webhook/ghl/trigger-retell-1?contact_id=${contact_id}`, {
      credentials: "include",
      method: "GET"
    })

    if (!res.ok)
      throw Error(res.statusText)

    const response = await res.json()

    if (response.success)
      return response.data

    console.log(response.error)

    throw Error(response.msg)
  } catch (error) {
    console.log(error)
  }
}


export async function getUser(contact_id) {
  const res = await fetch(`https://app.finaldebtrelief.com/api/users/${contact_id}`)
  const response = await res.json()
  if(response.success)
    return response.data?.state ?? null
  return null
}

export function determineOutboundNumber(state) {
  const number = CallAgentsNumbersByState.get(state) ?? agentsNumbers[0] ?? '+18557804538'
  return formatUSPhoneNumber(number)
}